import React, { useState, useEffect } from 'react';
import UserManagement from './UserManagement';
import Display from './Display';
import DemoClass from './DemoClass';
import AdminTab from './AdminTab';
import UserCards from './UserCards';
import EnrollmentForm from './EnrollmentForm';
import SessionScheduler from './SessionScheduler';
import Variables from './Variables';
import ListAllUsers from './ListAllUsers';
import EnrollmentsList from './EnrollmentsList';
import EnrollmentTable from './EnrollmentTable';
import GeneralMessages from './GeneralMessages';
import DocumentUpload from './DocumentUpload';

const AdministratorPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check screen size on component mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false); // Close sidebar by default on mobile
      } else {
        setSidebarOpen(true); // Open sidebar by default on desktop
      }
    };
    
    // Initial check
    checkScreenSize();
    
    // Add event listener
    window.addEventListener('resize', checkScreenSize);
    
    // Clean up
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Sample data for the admin dashboard
  const [platformStats] = useState([
    { title: 'Total Users', value: '1,248', icon: 'fas fa-users', trend: '↗︎ 12% this month', color: 'text-primary' },
    { title: 'Active Courses', value: '86', icon: 'fas fa-book', trend: '↗︎ 5 new courses', color: 'text-secondary' },
    { title: 'Monthly Revenue', value: '$8,420', icon: 'fas fa-dollar-sign', trend: '↗︎ 8% from last month', color: 'text-accent' },
    { title: 'Completion Rate', value: '78%', icon: 'fas fa-chart-line', trend: '↗︎ 3% improvement', color: 'text-info' }
  ]);

  const [recentUsers] = useState([
    {
      id: 1,
      name: 'Emily Chen',
      email: 'emily@example.com',
      role: 'Student',
      joinDate: '2023-09-15',
      status: 'active',
      avatar: 'https://randomuser.me/api/portraits/women/68.jpg'
    },
    {
      id: 2,
      name: 'Dr. Sarah Johnson',
      email: 'sarah@example.com',
      role: 'Tutor',
      joinDate: '2023-08-22',
      status: 'active',
      avatar: 'https://randomuser.me/api/portraits/women/12.jpg'
    },
    {
      id: 3,
      name: 'Michael Torres',
      email: 'michael@example.com',
      role: 'Student',
      joinDate: '2023-10-05',
      status: 'pending',
      avatar: 'https://randomuser.me/api/portraits/men/22.jpg'
    },
    {
      id: 4,
      name: 'Prof. James Wilson',
      email: 'james@example.com',
      role: 'Tutor',
      joinDate: '2023-07-18',
      status: 'active',
      avatar: 'https://randomuser.me/api/portraits/men/75.jpg'
    }
  ]);

  const [recentCourses] = useState([
    {
      id: 1,
      title: 'Calculus 101',
      tutor: 'Dr. Sarah Johnson',
      students: 42,
      price: '$49.99',
      rating: 4.7,
      status: 'published'
    },
    {
      id: 2,
      title: 'Python Fundamentals',
      tutor: 'Prof. James Wilson',
      students: 68,
      price: '$39.99',
      rating: 4.8,
      status: 'published'
    },
    {
      id: 3,
      title: 'Organic Chemistry',
      tutor: 'Dr. Lisa Anderson',
      students: 24,
      price: '$59.99',
      rating: 4.5,
      status: 'draft'
    }
  ]);

  const [revenueData] = useState([
    { month: 'Jan', revenue: 5200 },
    { month: 'Feb', revenue: 6100 },
    { month: 'Mar', revenue: 5900 },
    { month: 'Apr', revenue: 6500 },
    { month: 'May', revenue: 7200 },
    { month: 'Jun', revenue: 7800 },
    { month: 'Jul', revenue: 8420 }
  ]);

  const [userDistribution] = useState([
    { role: 'Students', count: 984, percentage: 79 },
    { role: 'Tutors', count: 214, percentage: 17 },
    { role: 'Admins', count: 5, percentage: 0.4 },
    { role: 'Pending', count: 45, percentage: 3.6 }
  ]);

  return (
    <div className="p-0 flex flex-col min-h-screen bg-base-100">
      {/* Mobile Navbar Toggle */}
      <div className="lg:hidden navbar bg-base-100 shadow-sm">
        <div className="flex-none">
          <button 
            className="btn btn-square btn-ghost"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-6 h-6 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>
        <div className="flex-1 px-2 mx-2">
          <span className="text-lg font-bold">LearnHub Admin</span>
        </div>
      </div>
   
      {/* Main Content */}
      <div className="flex flex-1">
        {/* Sidebar Overlay for Mobile */}
        {sidebarOpen && isMobile && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
        
        {/* Sidebar */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
          transform transition-transform duration-300 ease-in-out lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          w-64 bg-base-200 h-full flex-shrink-0
        `}>
          <div className="p-4 h-full overflow-y-auto">
            <div className="flex flex-col items-center py-6">
              <div className="avatar">
                <div className="w-20 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                  <h1 className='text-7xl flex items-center justify-center '>👤</h1>
                </div>
              </div>
              <h2 className="text-xl font-bold mt-4">Admin</h2>
              <p className="text-sm text-gray-500">Administrator</p>
              <div className="mt-2">
                <div className="badge badge-primary">Super Admin</div>
              </div>
            </div>

            <ul className="menu p-0">
              <li className="menu-title">Admin Panel</li>
              <li>
                <a 
                  className={activeTab === 'dashboard' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('dashboard');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-tachometer-alt mr-3"></i> Dashboard
                </a>
              </li>
              <li>
                <a 
                  className={activeTab === 'users' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('users');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-users mr-3"></i> User Registration
                </a>
              </li>
              <li>
                <a 
                  className={activeTab === 'courses' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('courses');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-book mr-3"></i> Courses
                </a>
              </li>
              <li>
                <a 
                  className={activeTab === 'sessions' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('sessions');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-chart-bar mr-3"></i> Sessions
                </a>
              </li>
              <li>
                <a 
                  className={activeTab === 'messages' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('messages');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-chart-bar mr-3"></i> Messages
                </a>
              </li>
             
              {/* <li>
                <a 
                  className={activeTab === 'payments' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('payments');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-dollar-sign mr-3"></i> Payments
                </a>
              </li> */}
            </ul>
            <ul className="menu p-0">
              <li className="menu-title">Mentor Panel</li>
              <li>
                <a 
                  className={activeTab === 'democlass' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('democlass');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-tachometer-alt mr-3"></i> Demo Classes
                </a>
              </li>
               <li>
                <a 
                  className={activeTab === 'documents' ? 'active' : ''} 
                  onClick={() => {
                    setActiveTab('documents');
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-chart-bar mr-3"></i> Documents
                </a>
              </li>
              </ul>
          </div>
           
        </div>

        {/* Content Area */}
        <div className="flex-1 p-4 lg:p-6 w-full">
          {activeTab === "users" && <AdminTab/>}
          {activeTab === "dashboard" && <ListAllUsers/>}
          {activeTab === "courses" && <EnrollmentForm />}
          {activeTab === "sessions" && <SessionScheduler />}
          {activeTab === "messages" && <GeneralMessages flag={true} />}
          {activeTab === "documents" && <DocumentUpload flag={true} />}
          {activeTab === "payments" && <EnrollmentTable />}
          {activeTab === "democlass" && <DemoClass />}
        </div>
        
      </div>
      


      {/* Footer */}
      <footer className="footer footer-center p-4 bg-neutral text-neutral-content">
        <div>
          <p>© 2023 LearnHub - Admin Dashboard v2.1.4</p>
        </div>
      </footer>
      {/* <Variables/> */}
    </div>
  );
};

export default AdministratorPage;